/**
 * @fileoverview ClubOS User Access Management — wired to real API
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import FootballLoader from "components/FootballLoader";

import { useAuth } from "context/AuthContext";
import { userService } from "api";
import { formatDateIN } from "utils/indiaLocale";

const ROLE_OPTIONS = [
  { value: "Admin",                 label: "Admin" },
  { value: "ClubCeo",               label: "Club CEO" },
  { value: "ChiefFinancialOfficer", label: "CFO" },
  { value: "DirectorOfFootball",    label: "Director of Football" },
  { value: "HeadCoach",             label: "Head Coach" },
  { value: "AssistantCoach",        label: "Assistant Coach" },
  { value: "ChiefScout",            label: "Chief Scout" },
  { value: "Scout",                 label: "Scout" },
  { value: "TeamDoctor",            label: "Team Doctor" },
  { value: "Physiotherapist",       label: "Physiotherapist" },
  { value: "ClubSecretary",         label: "Club Secretary" },
  { value: "OperationsManager",     label: "Operations Manager" },
  { value: "Analyst",               label: "Analyst" },
];

const DEPARTMENTS = [
  "Management", "Finance", "Coaching", "Scouting",
  "Medical", "Operations", "IT", "Administration", "Football", "Media",
];

const EMPTY_FORM = { fullName: "", email: "", password: "", role: "", department: "", phoneNumber: "" };

function validateForm(form, isEdit) {
  const e = {};
  if (!form.fullName.trim())   e.fullName = "Full name is required.";
  if (!form.email.trim())      e.email = "Email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email.";
  if (!isEdit && !form.password.trim()) e.password = "Password is required.";
  if (!isEdit && form.password && form.password.length < 8) e.password = "Min 8 characters.";
  if (!form.role)              e.role = "Role is required.";
  return e;
}

function UsersPage() {
  const { currentUser } = useAuth();

  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  // Filters
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [formErrors, setFormErrors]   = useState({});
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState("");

  // Deactivate confirm
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // Change password dialog
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [pwdTarget, setPwdTarget]         = useState(null);
  const [newPassword, setNewPassword]     = useState("");
  const [pwdError, setPwdError]           = useState("");
  const [pwdSaving, setPwdSaving]         = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    userService.getUsers()
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setError("Failed to load users."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => users.filter((u) => {
    const matchSearch = u.fullName.toLowerCase().includes(search.toLowerCase()) ||
                        u.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "All" ||
                        (statusFilter === "Active" && u.isActive) ||
                        (statusFilter === "Inactive" && !u.isActive);
    return matchSearch && matchStatus;
  }), [users, search, statusFilter]);

  const active   = users.filter((u) => u.isActive).length;
  const inactive = users.filter((u) => !u.isActive).length;

  // ── Add / Edit ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null); setForm(EMPTY_FORM); setFormErrors({}); setSaveError(""); setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ fullName: u.fullName, email: u.email, password: "", role: u.role, department: u.department ?? "", phoneNumber: u.phoneNumber ?? "" });
    setFormErrors({}); setSaveError(""); setDialogOpen(true);
  };

  const f = (key) => (e) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
    setFormErrors((p) => ({ ...p, [key]: undefined }));
    setSaveError("");
  };

  const handleSave = async () => {
    const errs = validateForm(form, Boolean(editing));
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setSaving(true); setSaveError("");
    try {
      if (editing) {
        await userService.updateUser(editing.id, {
          userId: editing.id,
          fullName: form.fullName,
          role: form.role,
          department: form.department || null,
          phoneNumber: form.phoneNumber || null,
          isActive: editing.isActive,
        });
      } else {
        await userService.createUser({
          fullName: form.fullName,
          email: form.email,
          password: form.password,
          role: form.role,
          department: form.department || null,
          phoneNumber: form.phoneNumber || null,
          tenantId: currentUser.tenantId,
        });
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setSaveError(err?.detail ?? err?.title ?? err?.errors?.detail ?? "Save failed.");
    } finally { setSaving(false); }
  };

  // ── Deactivate ────────────────────────────────────────────────────────────
  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await userService.deactivateUser(deactivateTarget.id);
      setDeactivateTarget(null);
      load();
    } catch { setDeactivateTarget(null); }
  };

  // ── Admin reset password ───────────────────────────────────────────────────
  const openPwdReset = (u) => { setPwdTarget(u); setNewPassword(""); setPwdError(""); setPwdDialogOpen(true); };

  const handlePwdReset = async () => {
    if (newPassword.length < 8) { setPwdError("Min 8 characters."); return; }
    setPwdSaving(true); setPwdError("");
    try {
      await userService.adminResetPassword(pwdTarget.id, newPassword);
      setPwdDialogOpen(false);
    } catch (err) {
      setPwdError(err?.detail ?? err?.title ?? "Failed to reset password.");
    } finally { setPwdSaving(false); }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        {/* Header */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">User Access Management</SoftTypography>
            <SoftTypography variant="body2" color="text">Manage platform users and roles</SoftTypography>
          </SoftBox>
          <SoftButton variant="gradient" color="info" onClick={openAdd}>
            <Icon sx={{ mr: 0.5 }}>person_add</Icon> Add User
          </SoftButton>
        </SoftBox>

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={4}>
              <MiniStatisticsCard title={{ text: "Total Users" }} count={String(users.length)}
                percentage={{ color: "info", text: "platform accounts" }} icon={{ color: "info", component: "group" }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <MiniStatisticsCard title={{ text: "Active" }} count={String(active)}
                percentage={{ color: "success", text: "can log in" }} icon={{ color: "success", component: "check_circle" }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <MiniStatisticsCard title={{ text: "Inactive" }} count={String(inactive)}
                percentage={{ color: "error", text: "deactivated" }} icon={{ color: "error", component: "person_off" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Table */}
        <Card>
          <SoftBox p={3}>
            {/* Filters */}
            <Grid container spacing={2} mb={3} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField fullWidth placeholder="Search by name or email…" value={search}
                  onChange={(e) => setSearch(e.target.value)} size="small"
                  InputProps={{ startAdornment: <Icon sx={{ mr: 1, color: "#aaa", fontSize: "1.1rem" }}>search</Icon> }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4}>
                <SoftTypography variant="caption" color="text">{filtered.length} of {users.length} users</SoftTypography>
              </Grid>
            </Grid>

            {loading ? <FootballLoader label="Loading users…" /> : (
              <SoftBox sx={{ overflowX: "auto" }}>
                <SoftBox component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                  <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                    <SoftBox component="tr">
                      {["User", "Email", "Role", "Department", "Status", "Last Login", "Actions"].map((h) => (
                        <SoftBox key={h} component="th" sx={{ textAlign: "left", p: 1.5, color: "text.secondary",
                          fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase",
                          borderBottom: "2px solid #e9ecef", whiteSpace: "nowrap" }}>{h}</SoftBox>
                      ))}
                    </SoftBox>
                  </SoftBox>
                  <SoftBox component="tbody">
                    {filtered.length === 0 ? (
                      <SoftBox component="tr">
                        <SoftBox component="td" colSpan={7} sx={{ textAlign: "center", p: 4 }}>
                          <SoftTypography variant="caption" color="text">No users found.</SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    ) : filtered.map((u, i) => (
                      <SoftBox key={u.id} component="tr"
                        sx={{ "&:hover": { backgroundColor: "#f8f9fa" }, backgroundColor: i % 2 === 0 ? "white" : "#fdfdfd" }}>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBox display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ width: 34, height: 34, fontSize: "0.75rem", fontWeight: 700, bgcolor: "#17c1e8" }}>
                              {u.fullName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                            </Avatar>
                            <SoftTypography variant="button" fontWeight="medium">{u.fullName}</SoftTypography>
                          </SoftBox>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="text">{u.email}</SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBadge badgeContent={u.role} color="info" size="xs" container />
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="text">{u.department ?? "—"}</SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <Chip label={u.isActive ? "Active" : "Inactive"} size="small"
                            color={u.isActive ? "success" : "default"} sx={{ fontSize: "0.65rem", height: 20 }} />
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="text">
                            {u.lastLoginAt ? formatDateIN(u.lastLoginAt) : "Never"}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBox display="flex" gap={0.5}>
                            <Tooltip title="Edit user">
                              <IconButton size="small" onClick={() => openEdit(u)}>
                                <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset password">
                              <IconButton size="small" color="warning" onClick={() => openPwdReset(u)}>
                                <Icon sx={{ fontSize: "1rem" }}>lock_reset</Icon>
                              </IconButton>
                            </Tooltip>
                            {u.isActive && (
                              <Tooltip title="Deactivate">
                                <IconButton size="small" color="error" onClick={() => setDeactivateTarget(u)}>
                                  <Icon sx={{ fontSize: "1rem" }}>person_off</Icon>
                                </IconButton>
                              </Tooltip>
                            )}
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>

      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Add / Edit Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? "Edit User" : "Add New User"}</DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Full Name" value={form.fullName} onChange={f("fullName")}
                size="small" error={!!formErrors.fullName} helperText={formErrors.fullName || " "} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Email" type="email" value={form.email}
                onChange={f("email")} size="small" disabled={Boolean(editing)}
                error={!!formErrors.email} helperText={formErrors.email || " "} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" value={form.phoneNumber} onChange={f("phoneNumber")}
                size="small" placeholder="+91 98765 43210" helperText=" " InputLabelProps={{ shrink: true }} />
            </Grid>
            {!editing && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Password" type="password" value={form.password}
                  onChange={f("password")} size="small"
                  error={!!formErrors.password} helperText={formErrors.password || " "} InputLabelProps={{ shrink: true }} />
              </Grid>
            )}
            <Grid item xs={12} sm={editing ? 6 : 6}>
              <FormControl fullWidth size="small" required error={!!formErrors.role}>
                <InputLabel shrink>Role</InputLabel>
                <Select value={form.role} label="Role" onChange={(e) => { setForm((p) => ({ ...p, role: e.target.value })); setFormErrors((p) => ({ ...p, role: undefined })); }}>
                  <MenuItem value="" disabled><em>Select role…</em></MenuItem>
                  {ROLE_OPTIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                </Select>
                <FormHelperText>{formErrors.role || " "}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Department</InputLabel>
                <Select value={form.department} label="Department" onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
                <FormHelperText> </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editing ? "Save Changes" : "Create User"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Deactivate Confirm ────────────────────────────────────────────────── */}
      <Dialog open={Boolean(deactivateTarget)} onClose={() => setDeactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "error.main" }}>Deactivate User</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text">
            Deactivate <strong>{deactivateTarget?.fullName}</strong>? They will no longer be able to log in.
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDeactivateTarget(null)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleDeactivate}>Deactivate</SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Admin Reset Password Dialog ───────────────────────────────────────── */}
      <Dialog open={pwdDialogOpen} onClose={() => setPwdDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reset Password — {pwdTarget?.fullName}</DialogTitle>
        <DialogContent dividers>
          {pwdError && <Alert severity="error" sx={{ mb: 2 }}>{pwdError}</Alert>}
          <TextField fullWidth label="New Password" type="password" value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setPwdError(""); }}
            size="small" helperText="Min 8 characters" sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setPwdDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="warning" onClick={handlePwdReset} disabled={pwdSaving}>
            {pwdSaving ? "Saving…" : "Reset Password"}
          </SoftButton>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

export default UsersPage;
